import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { DirectionService } from 'src/app/core/services/direction.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ValidationModalComponent } from 'src/app/shared/validation-modal/validation-modal.component';
import { AlerteModalComponent } from 'src/app/shared/alerte-modal/alerte-modal.component';
import { Observable, catchError, map, of, shareReplay, take } from 'rxjs';
import { ConfirmModalComponent } from 'src/app/shared/confirm-modal/confirm-modal.component';
import { AutocompleteFieldComponent } from 'src/app/shared/components/autocomplete-field/autocomplete-field.component';
import { AgentsService } from 'src/app/core/services/agents.service';
import { BaseUser } from 'src/app/core/models/shared/base-user.interface';

@Component({
    selector: 'app-edit-direction',
    imports: [
        CommonModule,
        MatIconModule,
        MatCardModule,
        MatDividerModule,
        MatDialogModule,
        MatButtonModule,
        MatInputModule,
        MatFormFieldModule,
        MatSelectModule,
        ReactiveFormsModule,
        DragDropModule,
        AutocompleteFieldComponent
    ],
    templateUrl: './edit-direction.component.html',
    styleUrls: ['./edit-direction.component.scss']
})
export class EditDirectionComponent implements OnInit {
  form: FormGroup;
  action: 'ajouter' | 'modifier' = 'ajouter';
  titre_action: string = 'Ajouter';
  selTypeWorkflow = '';
  readonly possibleWorkflows = [
    { libelle: 'Assistant vers Directeur', value: 'ASSISTANT_DIRECTEUR' },
    { libelle: 'Assistant vers Responsable puis Directeur', value: 'ASSISTANT_RESPONSABLE_DIRECTEUR' }
  ];
  rhRecrutementOptions$!: Observable<BaseUser[]>;
  rhRenouvellementOptions$!: Observable<BaseUser[]>;
  readonly recipientDisplayFn = (user: BaseUser | string | null): string => this.buildRecipientLabel(user);
  readonly recipientFilterFn = (user: BaseUser, searchText: string): boolean => {
    if (!searchText) {
      return true;
    }
    const normalizedSearch = (searchText || '').toLowerCase().replace(/\s+/g, '');
    const normalizedLabel = this.buildRecipientLabel(user).toLowerCase().replace(/\s+/g, '');
    return normalizedLabel.includes(normalizedSearch);
  };
  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly dialog: MatDialog,
    private readonly dialogRef: MatDialogRef<EditDirectionComponent>,
    @Inject(MAT_DIALOG_DATA) private readonly data: any,
    private readonly spinner: NgxSpinnerService,
    private readonly directionService: DirectionService,
    private readonly agentsService: AgentsService
  ) {}

  /** Initializes the form, determines dialog mode, and loads dropdown data. */
  ngOnInit(): void {
    this.buildForm();
    this.configureDialogMode();
    this.loadRecipients();
    this.form.markAsPristine();
  }

  /** Returns true when the user changed at least one editable control. */
  hasChanged(): boolean {
    return this.form.dirty;
  }

  /** Builds the reactive form with initial dialog values. */
  private buildForm(): void {
    this.form = this.formBuilder.group({
      libelle: [{ value: this.data?.libelle || '', disabled: true }],
      structValidation: [{ value: this.data?.structValidation || '', disabled: true }],
      typeWorkflow: [this.data?.typeWorkflow || '', [Validators.required]],
      structValidationGpeec: [this.data?.structValidationGpeec ?? null, [Validators.required]],
      structValidationStatutaire: [this.data?.structValidationStatutaire ?? null, [Validators.required]]
    });
    this.selTypeWorkflow = this.form.controls['typeWorkflow'].value ?? '';
  }

  /** Sets dialog title and mode according to the incoming data. */
  private configureDialogMode(): void {
    const edition = !!this.data?.edition;
    this.action = edition ? 'modifier' : 'ajouter';
    this.titre_action = edition ? 'Modifier' : 'Ajouter';
  }

  /** Handles closing the dialog, prompting the user when creating a new record with unsaved data. */
  closeDialog(): void {
    if (this.data?.edition) {
      this.dialogRef.close({ action: false });
      return;
    }
    this.promptCancelCreation();
  }

  /** Confirms that the user really wants to cancel a creation in progress. */
  private promptCancelCreation(): void {
    if (this.data?.edition) {
      return;
    }
    if (this.form.pristine) {
      const dialogConfirm = this.dialog.open(ConfirmModalComponent, {
        width: '600px',
        data: {
          title: 'Confirmation',
          content1: 'Souhaitez vous abandonner la création de la direction ?',
          content2: 'Les données que vous avez saisies ne seront pas enregistrées.'
        }
      });

      dialogConfirm.afterClosed().subscribe((shouldClose) => {
        if (shouldClose) {
          this.dialogRef.close({ action: false });
        }
      });
      return;
    }
    this.dialogRef.close({ action: false });
  }

  /** Validates the form and delegates to the update routine. */
  submitForm(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.updateDirection();
  }

  /** Sends the direction update payload to the backend service. */
  private updateDirection(): void {
    const directionId = this.data?.id;
    if (!directionId) {
      return;
    }

    const updatePayload = {
      id: directionId,
      libelle: this.form.controls['libelle'].value,
      structValidation: this.form.controls['structValidation'].value,
      typeWorkflow: this.form.controls['typeWorkflow'].value,
      structValidationGpeec: this.extractLogin(this.structValidationGpeecControl.value),
      structValidationStatutaire: this.extractLogin(this.structValidationStatutaireControl.value)
    };

    this.directionService
      .modifierDirection(updatePayload, directionId)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.spinner.hide();
          this.dialogRef.close({ action: true });
          this.dialog.open(ValidationModalComponent, {
            width: '600px',
            data: {
              title: 'Confirmation',
              content: 'La direction a été modifiée avec succès.'
            }
          });
        },
        error: (error) => {
          const erreur_texte = error.error?.detail || error.error || error.statusText || error;
          this.dialog.open(AlerteModalComponent, {
            width: '640px',
            data: {
              title: 'Alerte',
              content: 'Erreur lors de la modification de la direction !',
              status: 'STATUS : ' + error.status,
              detail: 'DETAIL : ' + erreur_texte
            }
          });
        }
      });
  }

  /** Loads both RH recipient dropdowns. */
  private loadRecipients(): void {
    const recipients$ = this.buildRecipientStream();

    this.rhRecrutementOptions$ = recipients$.pipe(
      map((list) => this.ensureCurrentRecipientPresent(list, this.structValidationGpeecControl))
    );

    this.rhRenouvellementOptions$ = recipients$.pipe(
      map((list) => this.ensureCurrentRecipientPresent(list, this.structValidationStatutaireControl))
    );
  }

  /** Retrieves RH recipients from the shared `/agents/all?type=rh` endpoint. */
  private buildRecipientStream(): Observable<BaseUser[]> {
    return this.agentsService.getAllAgentsDemandes('rh').pipe(
      take(1),
      map((response) => this.normalizeRecipientList(response)),
      catchError((error) => {
        console.error('Erreur lors du chargement des destinataires RH', error);
        this.dialog.open(AlerteModalComponent, {
          width: '640px',
          data: {
            title: 'Alerte',
            content: 'Erreur lors de la récupération des destinataires RH.',
            status: 'STATUS : ' + error.status,
            detail: 'DETAIL : ' + (error.error?.detail || error.error || error.statusText || error)
          }
        });
        return of([]);
      }),
      shareReplay(1)
    );
  }

  /** Normalizes the backend payload to a strongly typed list. */
  private normalizeRecipientList(data: unknown): BaseUser[] {
    if (!Array.isArray(data)) {
      console.warn('Format inattendu pour la liste des destinataires RH', data);
      return [];
    }

    return data
      .map((item) => this.normalizeRecipientValue(item))
      .filter((recipient): recipient is BaseUser => !!recipient);
  }

  /** Ensures the currently selected recipient remains available in the option list. */
  private ensureCurrentRecipientPresent(list: BaseUser[], control: FormControl): BaseUser[] {
    const current = control.value;
    if (!current) {
      return list;
    }

    const normalized = this.normalizeRecipientValue(current);
    if (!normalized) {
      return list;
    }

    if (
      !list.some(
        (item: BaseUser) =>
          (item?.login && item.login === normalized.login) || (item?.id && item.id === normalized.id)
      )
    ) {
      control.setValue(normalized, { emitEvent: false });
      control.markAsPristine();
      return [normalized, ...list];
    }

    control.setValue(normalized, { emitEvent: false });
    control.markAsPristine();
    return list;
  }

  /** Normalizes the control value to a BaseUser object for downstream consumption. */
  private normalizeRecipientValue(value: unknown): BaseUser | null {
    if (!value) {
      return null;
    }
    if (typeof value === 'string') {
      const normalizedValue = value.trim();
      if (!normalizedValue) {
        return null;
      }
      return {
        id: normalizedValue,
        login: normalizedValue,
        nom: '',
        prenom: '',
        email: null,
        numSecSociale: null,
        matricule: null
      };
    }
    if (typeof value === 'object') {
      const candidate = value as Partial<BaseUser> & { identifiant?: string | number };
      const rawLogin = candidate.login ?? candidate.identifiant ?? candidate.id;
      const login = rawLogin != null ? String(rawLogin) : '';
      if (!login) {
        return null;
      }
      return {
        id: candidate.id != null ? String(candidate.id) : login,
        login,
        nom: candidate.nom ?? '',
        prenom: candidate.prenom ?? '',
        email: candidate.email ?? null,
        numSecSociale: candidate.numSecSociale ?? null,
        matricule: candidate.matricule ?? null
      };
    }
    return null;
  }

  /** Formats a recipient for display inside the virtual autocomplete. */
  private buildRecipientLabel(recipient: BaseUser | string | null): string {
    if (!recipient) {
      return '';
    }
    if (typeof recipient === 'string') {
      return recipient;
    }
    const rawLogin = recipient.login ?? recipient.id ?? '';
    const login = rawLogin ? String(rawLogin) : '';
    const nom = recipient.nom ?? '';
    const prenom = recipient.prenom ?? '';
    const fullName = [nom, prenom].filter(Boolean).join(' ').trim();
    return fullName && login ? `${fullName} (${login})` : fullName || login;
  }

  /** Extracts the login value from the control payload before sending it to the backend. */
  private extractLogin(value: unknown): string | null {
    const normalized = this.normalizeRecipientValue(value);
    if (!normalized) {
      return null;
    }
    return normalized.login || normalized.id || null;
  }

  /** Convenience accessor for the recrutement RH control. */
  get structValidationGpeecControl(): FormControl {
    return this.form.get('structValidationGpeec') as FormControl;
  }

  /** Convenience accessor for the renouvellement RH control. */
  get structValidationStatutaireControl(): FormControl {
    return this.form.get('structValidationStatutaire') as FormControl;
  }
}
